import type { ProjectDto, ProjectSummaryDto } from '@studio/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Clapperboard, Wand2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/form';
import { PageHeader } from '@/components/ui/misc';
import { api, errorMessage } from '@/lib/api';
import { ago } from '@/lib/utils';

/** The Director: pick a project (or start one) and let ChatGPT plan the film shot by shot. */
export function DirectorPage() {
  const nav = useNavigate();
  const projects = useQuery({ queryKey: ['projects'], queryFn: () => api<ProjectSummaryDto[]>('/api/projects') });
  const [title, setTitle] = useState('');
  const create = useMutation({
    mutationFn: () => api<ProjectDto>('/api/projects', { method: 'POST', body: { title } }),
    onSuccess: (p) => nav(`/projects/${p.id}?tab=director`),
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PageHeader
        title="Director"
        description="ChatGPT as your film director: from an idea or a script it plans every scene — camera, lens, movement, lighting, the characters' exact look, dialogue and sound. You edit everything, then send it to the storyboard and generate."
      />
      <Card>
        <CardHeader>
          <CardTitle>New film</CardTitle>
          <CardDescription>
            Starts a project in HD; product, characters and shape are chosen next to the brief.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 sm:flex-row">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title, e.g. Hair Booster Oil — monsoon ad"
          />
          <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!title.trim()}>
            <Wand2 /> Start directing
          </Button>
        </CardContent>
      </Card>
      <div>
        <div className="mb-2 text-sm font-medium">Or direct an existing project</div>
        <div className="grid gap-2 sm:grid-cols-2">
          {projects.data?.map((p) => (
            <Link key={p.id} to={`/projects/${p.id}?tab=director`}>
              <Card className="flex items-center gap-3 p-3 hover:bg-accent/40">
                <div className="grid h-14 w-9 shrink-0 place-items-center overflow-hidden rounded bg-muted">
                  {p.thumbnail ? (
                    <img src={p.thumbnail.url} alt="" className="size-full object-cover" />
                  ) : (
                    <Clapperboard className="size-4 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="truncate font-medium">{p.title}</div>
                  <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                    <Badge variant="secondary">{p.aspect}</Badge>
                    <Badge variant="secondary">{p.size === 'hd' ? 'HD' : 'Standard'}</Badge>
                    {p.shots} shots · {ago(p.updatedAt)}
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
